import { SaveDataSourceEnum } from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';
import DexfSaveService from '../../services/save-service/DexfSaveService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
/**
 * This class is used as a simple interface for Save Services.
 */
export default class SaveService {
    /**
     * Constructor.
     *
     * @param options ISaveServiceOptions.
     */
    constructor(options) {
        this.applicationName = options.applicationName;
        this.dexfApplicationId = options.dexfApplicationId || null;
        this.dexfConfigurationSchemaVersion = options.dexfConfigurationSchemaVersion;
        this.dexfConfiguration = new DexfConfiguration(options);
    }
    /**
     * Fetches and returns a save to profile configuration version.
     *
     * @param options IGetConfigurationOptions. Token for the user (optional) and id of the saved configuration version.
     * @returns Configuration: ISaveConfiguration.
     * @throws Will throw error if token is invalid or configuration can not be fetched.
     */
    async getConfiguration(options) {
        const { token, id } = options;
        const service = await this.getService();
        return await service.getConfiguration({ token, id });
    }
    /**
     * Fetches and returns a list of configurations the user have saved to profile.
     *
     * @param options IGetConfigurationsOptions. Token for the user and different filters for what configurations to fetch. See type for more detailed information.
     * @returns Array of ISaveConfigurationsItem.
     * @throws Will throw error if token is invalid or fetching configurations fail.
     */
    async getConfigurations(options) {
        const service = await this.getService();
        const { token, filters } = options;
        return await service.getConfigurations(token, filters);
    }
    /**
     * Saves the planner configuration to profile for the user. A new configuration or a new version.
     *
     * @param options ISaveConfigurationOptions. Token for the user and different options for the configuration to save. See type for more detailed information.
     * @returns The configuration<ISaveConfigurationSave> if saved successfully.
     * @throws Will throw error if token is invalid or saving configuration fails.
     */
    async saveConfiguration(options) {
        const service = await this.getService();
        const { token, configurationOptions } = options;
        return await service.saveConfiguration(token, configurationOptions);
    }
    /**
     * Returns service.
     *
     * @returns Service.
     */
    async getService() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        const dexfApiKey = await this.dexfConfiguration.getDexfApiKeyOrThrow();
        switch (kompis.serviceSettings.saveDataSource) {
            case SaveDataSourceEnum.disabled:
                throw new ServiceException('The saveDataSource is set to "disabled". When it is, the SaveService is not available on the market and the save functionality together with related features, should be hidden.');
            default:
                return new DexfSaveService({
                    endpoint: kompis.endpoints.dexfSaveServiceV2,
                    dexfApplicationId: this.dexfApplicationId || this.applicationName,
                    dexfApiKey,
                    dexfConfigurationSchemaVersion: this.dexfConfigurationSchemaVersion
                });
        }
    }
}
//# sourceMappingURL=SaveService.js.map